/**
 * Canvas2D-backed renderer for the milestone-1 greybox.
 *
 * Pure 2D canvas — no Pixi, no WebGL, no async init that can hang on
 * GPU fallback issues. Replaced PixiRenderer at milestone 1.1 after
 * Pixi v8's Application.init() was found to hang on certain headless +
 * WebGPU-fallback paths. Greybox is just colored shapes, so the canvas
 * 2D context is the right level of abstraction for now.
 *
 * Visual style:
 *   - Ink outlines on every shape (matches the illustration reference).
 *   - Playfield sized to iPhone 17 Pro portrait viewport (402×874) with
 *     62 pt display corner radius; stroked border uses the same ink as
 *     cats and furniture.
 *   - Nightstand + lamp; floor shadow under the bed.
 *
 * Coordinate system: world y is positive-down (matches canvas + Rapier
 * gravity setup). All world dimensions come from BEDROOM_DEFAULT.
 */

import {
  ARCADE_SHELF,
  BEDROOM_DEFAULT,
  bedBottomY,
  bedDepth,
  bedTopY,
  getArchetype,
} from '@meowntain/content';
import type { BoredBehavior } from '@meowntain/core';
import type { CatState } from '@meowntain/core';
import {
  bugEyesActive,
  squishLevelFromRatio,
  squishScaleFromRatio,
  startleLevelFromImpulse,
} from '@meowntain/core';
import type { IRenderer } from './index.js';

/** Per-cat render state — survives across frames so the startle flash
 *  fires once per impact rather than every frame. */
interface CatVisualState {
  archetypeId: string;
  lastStartle: 'none' | 'small' | 'large';
  /** Wall-clock ms when the current tint flash started. 0 = no flash. */
  tintFlashStart: number;
  /** Initial tint color (0xRRGGBB) — fades back to base over ~200 ms. */
  tintFlashColor: number;
  bliss: number;
  holdActive: boolean;
}

export interface CatSoothingVisual {
  bliss: number;
  holdActive: boolean;
  toyCurious?: boolean;
}

export interface ShelfVisualState {
  reachable: boolean;
  glint: boolean;
  toyOnShelf: boolean;
  toyExposed: boolean;
}

export interface WaitingCatVisual {
  queueId: string;
  archetypeId: string;
  slotIndex: number;
  patience: number;
  behavior: BoredBehavior | null;
  behaviorPhase: 'none' | 'telegraph' | 'active';
}

export interface QueueSlotPosition {
  x: number;
  y: number;
}

const TINT_FADE_MS = 200;

/** Warm near-black for ink outlines. Pure black is too harsh on white. */
const INK_COLOR = '#1f1d1c';
/** Outline width in world units. */
const INK_WIDTH = 1.5;
/** Cat eye color — same family as the ink. */
const EYE_COLOR = '#141019';

export class Canvas2DRenderer implements IRenderer {
  private host: HTMLElement | null = null;
  private canvas: HTMLCanvasElement | null = null;
  private ctx: CanvasRenderingContext2D | null = null;
  private background: HTMLCanvasElement | null = null;
  /** World→screen scale (uniform). */
  private worldScale = 1;
  /** World origin offset in screen pixels. */
  private worldOffsetX = 0;
  private worldOffsetY = 0;
  /** Cached devicePixelRatio for crisp drawing. */
  private dpr = 1;

  private catStates: readonly CatState[] = [];
  private catVisuals = new Map<string, CatVisualState>();
  private soothingVisuals = new Map<string, CatSoothingVisual>();
  private shelfVisual: ShelfVisualState = {
    reachable: false,
    glint: false,
    toyOnShelf: true,
    toyExposed: false,
  };
  private waitingCats: WaitingCatVisual[] = [];
  private queueSlots: QueueSlotPosition[] = [];
  private heldCat: { archetypeId: string; x: number; y: number } | null = null;

  private resizeObserver: ResizeObserver | null = null;

  init(host: HTMLElement): Promise<void> {
    this.host = host;
    this.dpr = window.devicePixelRatio || 1;

    this.canvas = document.createElement('canvas');
    this.canvas.style.display = 'block';
    this.canvas.style.width = '100%';
    this.canvas.style.height = '100%';
    this.canvas.style.touchAction = 'none';
    host.appendChild(this.canvas);

    const ctx = this.canvas.getContext('2d', { alpha: false });
    if (!ctx) throw new Error('Canvas2D context not available');
    this.ctx = ctx;

    this.resize();
    this.bakeBackground();

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.resize());
      this.resizeObserver.observe(host);
    } else {
      window.addEventListener('resize', () => this.resize());
    }

    return Promise.resolve();
  }

  /** Resize the canvas backing buffer to host * dpr, recompute world fit. */
  private resize(): void {
    if (!this.host || !this.canvas) return;
    const rect = this.host.getBoundingClientRect();
    const cssW = Math.max(1, Math.round(rect.width));
    const cssH = Math.max(1, Math.round(rect.height));
    this.canvas.width = Math.round(cssW * this.dpr);
    this.canvas.height = Math.round(cssH * this.dpr);

    const worldW = BEDROOM_DEFAULT.bedWidth;
    const worldH = BEDROOM_DEFAULT.playfieldHeight;
    this.worldScale = Math.min(cssW / worldW, cssH / worldH);
    this.worldOffsetX = (cssW - worldW * this.worldScale) / 2;
    this.worldOffsetY = (cssH - worldH * this.worldScale) / 2;
  }

  /** Bake the static bedroom into an offscreen canvas. Done once at init
   *  in world coordinates; blitted with transform each frame. */
  private bakeBackground(): void {
    const worldW = BEDROOM_DEFAULT.bedWidth;
    const worldH = BEDROOM_DEFAULT.playfieldHeight;
    const bg = document.createElement('canvas');
    bg.width = worldW;
    bg.height = worldH;
    const bgCtx = bg.getContext('2d');
    if (!bgCtx) return;

    bgCtx.lineJoin = 'round';
    bgCtx.lineCap = 'round';

    bgCtx.fillStyle = colorHex(BEDROOM_DEFAULT.bgColor);
    bgCtx.fillRect(0, 0, worldW, worldH);

    const surfaceY = bedTopY(worldH);
    const footY = bedBottomY(worldH);
    const depth = bedDepth(worldH);

    bgCtx.fillStyle = 'rgba(0,0,0,0.07)';
    bgCtx.beginPath();
    bgCtx.ellipse(worldW * 0.5, footY - 10, worldW * 0.42, 8, 0, 0, Math.PI * 2);
    bgCtx.fill();

    fillAndStroke(
      bgCtx,
      () => roundRectPath(bgCtx, 8, surfaceY + 6, worldW - 16, depth - 12, 18),
      colorHex(BEDROOM_DEFAULT.sleeperColor),
    );

    bgCtx.fillStyle = colorHex(BEDROOM_DEFAULT.blanketColor);
    bgCtx.beginPath();
    bgCtx.moveTo(0, surfaceY);
    for (const seg of BEDROOM_DEFAULT.segments) {
      const renderY = surfaceY - seg.y * 0.4;
      bgCtx.lineTo(seg.x, renderY);
    }
    bgCtx.lineTo(worldW, footY);
    bgCtx.lineTo(0, footY);
    bgCtx.closePath();
    bgCtx.fill();

    bgCtx.strokeStyle = INK_COLOR;
    bgCtx.lineWidth = INK_WIDTH;
    bgCtx.beginPath();
    bgCtx.moveTo(0, surfaceY);
    for (const seg of BEDROOM_DEFAULT.segments) {
      const renderY = surfaceY - seg.y * 0.4;
      bgCtx.lineTo(seg.x, renderY);
    }
    bgCtx.lineTo(worldW, footY);
    bgCtx.stroke();

    fillAndStroke(
      bgCtx,
      () => roundRectPath(bgCtx, worldW - 18, surfaceY - 26, 18, 110, 4),
      '#9b7d5b',
    );

    const headX = worldW * 0.13;
    const headY = surfaceY - 6;
    fillAndStroke(
      bgCtx,
      () => {
        bgCtx.beginPath();
        bgCtx.arc(headX, headY, 22, 0, Math.PI * 2);
      },
      '#e8d6b9',
    );
    bgCtx.strokeStyle = INK_COLOR;
    bgCtx.lineWidth = 1.4;
    bgCtx.lineCap = 'round';
    bgCtx.beginPath();
    bgCtx.arc(headX - 6, headY - 1, 3.5, Math.PI * 0.15, Math.PI * 0.85);
    bgCtx.arc(headX + 6, headY - 1, 3.5, Math.PI * 0.15, Math.PI * 0.85);
    bgCtx.stroke();

    const nsW = 54;
    const nsH = 78;
    const nsX = 8 - nsW + 4;
    const nsY = surfaceY + 18;
    fillAndStroke(bgCtx, () => roundRectPath(bgCtx, nsX, nsY, nsW, nsH, 5), '#a07b58');
    bgCtx.strokeStyle = INK_COLOR;
    bgCtx.lineWidth = 1.2;
    bgCtx.beginPath();
    bgCtx.moveTo(nsX + 6, nsY + 30);
    bgCtx.lineTo(nsX + nsW - 6, nsY + 30);
    bgCtx.stroke();

    const lampX = nsX + nsW / 2;
    const lampBaseY = nsY - 2;
    fillAndStroke(
      bgCtx,
      () => roundRectPath(bgCtx, lampX - 5, lampBaseY - 8, 10, 10, 3),
      '#d8d4cd',
    );
    fillAndStroke(
      bgCtx,
      () => {
        bgCtx.beginPath();
        bgCtx.moveTo(lampX - 14, lampBaseY - 14);
        bgCtx.lineTo(lampX + 14, lampBaseY - 14);
        bgCtx.lineTo(lampX + 9, lampBaseY - 30);
        bgCtx.lineTo(lampX - 9, lampBaseY - 30);
        bgCtx.closePath();
      },
      '#e9e0cf',
    );

    const shelf = ARCADE_SHELF;
    fillAndStroke(
      bgCtx,
      () => roundRectPath(bgCtx, shelf.x, shelf.y, shelf.width, shelf.height, 4),
      '#c4a882',
    );
    bgCtx.fillStyle = INK_COLOR;
    bgCtx.font = 'bold 9px system-ui, sans-serif';
    bgCtx.textAlign = 'center';
    bgCtx.textBaseline = 'middle';
    bgCtx.fillText('T', shelf.x + shelf.width * 0.25, shelf.y + shelf.height / 2);
    bgCtx.fillText('?', shelf.x + shelf.width * 0.75, shelf.y + shelf.height / 2);

    this.background = bg;
  }

  updateShelfVisual(state: ShelfVisualState): void {
    this.shelfVisual = state;
  }

  updateWaitingCats(
    cats: readonly WaitingCatVisual[],
    slots: readonly QueueSlotPosition[],
    held: { archetypeId: string; x: number; y: number } | null,
  ): void {
    this.waitingCats = [...cats];
    this.queueSlots = [...slots];
    this.heldCat = held;
  }

  updateCats(states: readonly CatState[]): void {
    this.catStates = states;
    const now = performance.now();
    for (const state of states) {
      let v = this.catVisuals.get(state.catId);
      if (!v) {
        v = {
          archetypeId: state.archetypeId,
          lastStartle: 'none',
          tintFlashStart: 0,
          tintFlashColor: 0xffffff,
          bliss: 0,
          holdActive: false,
        };
        this.catVisuals.set(state.catId, v);
      }
      const startle = startleLevelFromImpulse(state.lastImpactImpulse);
      if (startle !== 'none' && v.lastStartle === 'none') {
        v.tintFlashStart = now;
        v.tintFlashColor = startle === 'large' ? 0xfff2c4 : 0xfff8e0;
      }
      v.lastStartle = startle;
    }
    const seen = new Set(states.map((s) => s.catId));
    for (const id of this.catVisuals.keys()) {
      if (!seen.has(id)) this.catVisuals.delete(id);
    }
  }

  updateCatSoothing(visuals: ReadonlyMap<string, CatSoothingVisual>): void {
    this.soothingVisuals = new Map(visuals);
    for (const [id, v] of this.catVisuals) {
      const s = visuals.get(id);
      if (s) {
        v.bliss = s.bliss;
        v.holdActive = s.holdActive;
      }
    }
  }

  render(): void {
    if (!this.ctx || !this.canvas) return;
    const ctx = this.ctx;
    const worldW = BEDROOM_DEFAULT.bedWidth;
    const worldH = BEDROOM_DEFAULT.playfieldHeight;
    const cornerR = BEDROOM_DEFAULT.cornerRadius;

    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);

    ctx.fillStyle = colorHex(BEDROOM_DEFAULT.bgColor);
    const cssW = this.canvas.width / this.dpr;
    const cssH = this.canvas.height / this.dpr;
    ctx.fillRect(0, 0, cssW, cssH);

    ctx.translate(this.worldOffsetX, this.worldOffsetY);
    ctx.scale(this.worldScale, this.worldScale);

    ctx.save();
    clipPlayfield(ctx, worldW, worldH, cornerR);

    if (this.background) {
      ctx.drawImage(this.background, 0, 0);
    }

    const now = performance.now();
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    for (const state of this.catStates) {
      this.drawCat(ctx, state, now);
    }

    this.drawShelfOverlay(ctx, now);
    this.drawWaitingCats(ctx);

    ctx.restore();

    strokePlayfieldBorder(ctx, worldW, worldH, cornerR);
  }

  private drawCat(ctx: CanvasRenderingContext2D, state: CatState, now: number): void {
    const archetype = getArchetype(state.archetypeId);
    const visual = this.catVisuals.get(state.catId);
    const soothing = this.soothingVisuals.get(state.catId);
    const bliss = soothing?.bliss ?? visual?.bliss ?? 0;
    const holdActive = soothing?.holdActive ?? visual?.holdActive ?? false;

    const squishY = squishScaleFromRatio(state.pressureRatio);
    const sLevel = squishLevelFromRatio(state.pressureRatio);
    const widen = sLevel === 'overload' ? 1.06 : 1.0;

    let baseColor = archetype.greyboxColor;
    if (visual && visual.tintFlashStart > 0) {
      const t = Math.min(1, (now - visual.tintFlashStart) / TINT_FADE_MS);
      if (t >= 1) {
        visual.tintFlashStart = 0;
      } else {
        baseColor = blendMultiplyTint(archetype.greyboxColor, visual.tintFlashColor, t);
      }
    }

    if (holdActive || bliss > 0.15) {
      const glowR = Math.max(archetype.width, archetype.height) * (0.55 + bliss * 0.15);
      const alpha = holdActive ? 0.22 + bliss * 0.2 : bliss * 0.18;
      ctx.save();
      ctx.translate(state.x, state.y);
      ctx.fillStyle =
        bliss >= 1 ? `rgba(255, 210, 140, ${alpha})` : `rgba(200, 180, 255, ${alpha})`;
      ctx.beginPath();
      ctx.arc(0, 0, glowR, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }

    ctx.save();
    ctx.translate(state.x, state.y);
    ctx.rotate(state.rotation);
    ctx.scale(widen, squishY);

    const halfW = archetype.width / 2;
    const halfH = archetype.height / 2;
    const r = Math.min(halfW, halfH) * 0.45;
    const fillColor = colorHex(baseColor);

    if (sLevel !== 'overload') {
      const earW = archetype.width * 0.13;
      const earH = Math.min(archetype.height * 0.4, archetype.width * 0.22);
      const earY = -halfH;
      const earOffsetX = archetype.width * 0.22;
      ctx.fillStyle = fillColor;
      ctx.strokeStyle = INK_COLOR;
      ctx.lineWidth = INK_WIDTH;
      ctx.beginPath();
      ctx.moveTo(-earOffsetX - earW, earY + 2);
      ctx.lineTo(-earOffsetX + earW, earY + 2);
      ctx.lineTo(-earOffsetX, earY - earH);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(earOffsetX - earW, earY + 2);
      ctx.lineTo(earOffsetX + earW, earY + 2);
      ctx.lineTo(earOffsetX, earY - earH);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
    }

    fillAndStroke(
      ctx,
      () => roundRectPath(ctx, -halfW, -halfH, archetype.width, archetype.height, r),
      fillColor,
    );

    const bugEyes = bugEyesActive(state.pressureRatio);
    const eyeRadius = Math.max(1.2, archetype.width * 0.04) * (bugEyes ? 1.6 : 1.0);
    const eyeY = -archetype.height * 0.18;
    const eyeSpacing = archetype.width * 0.22;
    ctx.fillStyle = EYE_COLOR;
    ctx.beginPath();
    ctx.arc(-eyeSpacing, eyeY, eyeRadius, 0, Math.PI * 2);
    ctx.arc(eyeSpacing, eyeY, eyeRadius, 0, Math.PI * 2);
    ctx.fill();

    if (soothing?.toyCurious) {
      ctx.save();
      ctx.translate(0, -halfH - 14);
      ctx.fillStyle = INK_COLOR;
      ctx.font = 'bold 14px system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('?', 0, 0);
      ctx.restore();
    }

    ctx.restore();
  }

  private drawWaitingCats(ctx: CanvasRenderingContext2D): void {
    const behaviorLabel: Partial<Record<BoredBehavior, string>> = {
      bat_sleeper: 'bat',
      yowl: 'yowl',
      play_toy: 'toy',
      knead_pillow: 'knead',
    };

    for (const cat of this.waitingCats) {
      const slot = this.queueSlots[cat.slotIndex];
      if (!slot) continue;
      const archetype = getArchetype(cat.archetypeId);
      const halfW = archetype.width / 2;
      const halfH = archetype.height / 2;
      ctx.save();
      ctx.translate(slot.x, slot.y);
      const fill = colorHex(archetype.greyboxColor);
      fillAndStroke(
        ctx,
        () => roundRectPath(ctx, -halfW, -halfH, archetype.width, archetype.height, 8),
        fill,
      );
      if (cat.patience > 0.15 && !cat.behavior) {
        const pw = archetype.width * cat.patience;
        ctx.fillStyle =
          cat.patience > 0.85 ? 'rgba(232, 93, 74, 0.5)' : 'rgba(106, 143, 123, 0.45)';
        ctx.fillRect(-halfW, halfH + 4, pw, 4);
      }
      if (cat.behavior === 'walk_away' && cat.behaviorPhase === 'telegraph') {
        ctx.fillStyle = INK_COLOR;
        ctx.font = '10px system-ui, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('…', 0, -halfH - 8);
      } else if (cat.behavior) {
        const label = behaviorLabel[cat.behavior];
        if (label) {
          ctx.fillStyle = INK_COLOR;
          ctx.font = 'bold 10px system-ui, sans-serif';
          ctx.textAlign = 'center';
          ctx.fillText(label, 0, -halfH - 8);
        }
      }
      ctx.restore();
    }

    if (this.heldCat) {
      const archetype = getArchetype(this.heldCat.archetypeId);
      const halfW = archetype.width / 2;
      const halfH = archetype.height / 2;
      ctx.save();
      ctx.globalAlpha = 0.92;
      ctx.translate(this.heldCat.x, this.heldCat.y - 8);
      fillAndStroke(
        ctx,
        () => roundRectPath(ctx, -halfW, -halfH, archetype.width, archetype.height, 8),
        colorHex(archetype.greyboxColor),
      );
      ctx.strokeStyle = 'rgba(106, 143, 123, 0.9)';
      ctx.lineWidth = 2;
      ctx.setLineDash([4, 3]);
      roundRectPath(ctx, -halfW - 4, -halfH - 4, archetype.width + 8, archetype.height + 8, 10);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.restore();
    }
  }

  private drawShelfOverlay(ctx: CanvasRenderingContext2D, now: number): void {
    const shelf = ARCADE_SHELF;
    const sv = this.shelfVisual;
    if (sv.reachable) {
      ctx.save();
      ctx.strokeStyle = sv.glint ? 'rgba(255, 210, 80, 0.9)' : 'rgba(106, 143, 123, 0.75)';
      ctx.lineWidth = sv.glint ? 2.5 : 1.8;
      roundRectPath(ctx, shelf.x - 2, shelf.y - 2, shelf.width + 4, shelf.height + 4, 6);
      ctx.stroke();
      ctx.restore();
    }
    if (sv.glint && sv.toyOnShelf) {
      const pulse = 0.5 + 0.5 * Math.sin(now / 120);
      ctx.save();
      ctx.fillStyle = `rgba(255, 230, 120, ${0.25 + pulse * 0.35})`;
      const tx = shelf.x + shelf.width * 0.75;
      const ty = shelf.y + shelf.height / 2;
      ctx.beginPath();
      ctx.arc(tx, ty, 12 + pulse * 4, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
    if (sv.toyExposed) {
      ctx.save();
      ctx.fillStyle = '#e85d4a';
      ctx.font = 'bold 10px system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('TOY OUT', shelf.x + shelf.width / 2, shelf.y - 8);
      ctx.restore();
    }
  }

  destroy(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    this.canvas?.remove();
    this.canvas = null;
    this.ctx = null;
    this.background = null;
    this.host = null;
    this.catStates = [];
    this.catVisuals.clear();
    this.soothingVisuals.clear();
  }
}

function clipPlayfield(ctx: CanvasRenderingContext2D, w: number, h: number, r: number): void {
  roundRectPath(ctx, 0, 0, w, h, r);
  ctx.clip();
}

function strokePlayfieldBorder(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  r: number,
): void {
  roundRectPath(ctx, 0, 0, w, h, r);
  ctx.strokeStyle = INK_COLOR;
  ctx.lineWidth = INK_WIDTH;
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  ctx.stroke();
}

function colorHex(color: number): string {
  return `#${color.toString(16).padStart(6, '0')}`;
}

function fillAndStroke(
  ctx: CanvasRenderingContext2D,
  buildPath: () => void,
  fill: string,
  stroke: string = INK_COLOR,
  lineWidth: number = INK_WIDTH,
): void {
  buildPath();
  ctx.fillStyle = fill;
  ctx.fill();
  ctx.strokeStyle = stroke;
  ctx.lineWidth = lineWidth;
  ctx.stroke();
}

function roundRectPath(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  const radius = Math.max(0, Math.min(r, w / 2, h / 2));
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + w - radius, y);
  ctx.arcTo(x + w, y, x + w, y + radius, radius);
  ctx.lineTo(x + w, y + h - radius);
  ctx.arcTo(x + w, y + h, x + w - radius, y + h, radius);
  ctx.lineTo(x + radius, y + h);
  ctx.arcTo(x, y + h, x, y + h - radius, radius);
  ctx.lineTo(x, y + radius);
  ctx.arcTo(x, y, x + radius, y, radius);
  ctx.closePath();
}

function blendMultiplyTint(base: number, flash: number, t: number): number {
  const fr = (flash >> 16) & 0xff;
  const fg = (flash >> 8) & 0xff;
  const fb = flash & 0xff;
  const tr = Math.round(fr + (255 - fr) * t);
  const tg = Math.round(fg + (255 - fg) * t);
  const tb = Math.round(fb + (255 - fb) * t);
  const br = (base >> 16) & 0xff;
  const bg = (base >> 8) & 0xff;
  const bb = base & 0xff;
  const or = Math.round((br * tr) / 255);
  const og = Math.round((bg * tg) / 255);
  const ob = Math.round((bb * tb) / 255);
  return (or << 16) | (og << 8) | ob;
}
